// --- React · 라우팅 ---
import React from 'react';
import { useNavigate } from 'react-router-dom';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 ---
import { useTheme } from '@emotion/react';
import type { AppTheme } from '@/theme/theme';
import { createAuthStyles } from '@/components/auth/authStyles';
import registerImage from '/images/register.png';

// --- 공통 UI 컴포넌트 ---
import { LanguageTabs } from '@/components/ui/common/LanguageTabs';
import { Button } from '@/components/ui';

// --- 스토어 ---
import { useNotificationStore } from '@/store/notificationStore';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// ==================== 상수 ====================

const SUPPORT_EMAIL = 'bellcloud-dev@gdi-solution.com';

// ==================== 컴포넌트 ====================

const FindEmail: React.FC = () => {
  const theme = useTheme() as AppTheme;
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const { showSuccess } = useNotificationStore();

  // ==================== 핸들러 ====================

  /** 언어 변경 */
  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  /**
   * 지원 이메일 클릭 시 클립보드에 복사 후 메일 클라이언트 열기.
   */
  const handleEmailClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      showSuccess('auth:email_copied', 'auth:email_copied_message');
    } catch (err) {
      console.error('Failed to copy email:', err);
    }

    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  };

  // ==================== 렌더링 ====================

  const styles = createAuthStyles(registerImage, theme);

  return (
    <div css={styles.container}>
      {/* 왼쪽 섹션 - 브랜드 이미지 (데스크톱 전용) */}
      <div css={styles.leftSection}>
        <div css={styles.leftOverlay} />
        <div css={styles.leftContent}>
          <div css={styles.leftText}>
            <img src="/images/bellcloud.svg" alt="BellCloud Logo" css={styles.logo} />
          </div>
        </div>
      </div>

      {/* 오른쪽 섹션 - 이메일 찾기 안내 */}
      <div css={styles.rightSection}>
        <div css={styles.formContainer}>
          {/* 타이틀 + 언어 선택 */}
          <div css={styles.header}>
            <div>
              <h2 css={styles.formTitle}>{t(i18nKeys.auth.find_email)}</h2>
              <div css={styles.formTitleLine} />
            </div>

            <div css={styles.headerLanguageTabs}>
              <LanguageTabs
                currentLanguage={i18n.language}
                onLanguageChange={handleLanguageChange}
              />
            </div>
          </div>

          {/* 관리자 연락 안내 콘텐츠 */}
          <div css={styles.contentArea}>
            <div css={styles.iconLarge}>📧</div>

            <p css={styles.messageText}>{t(i18nKeys.auth.contact_admin_for_email_recovery)}</p>

            {/* 지원 이메일 정보 박스 */}
            <div css={styles.infoBox}>
              <p css={styles.infoBoxLabel}>{t(i18nKeys.auth.support_email)}</p>
              <a href={`mailto:${SUPPORT_EMAIL}`} onClick={handleEmailClick} css={styles.emailLink}>
                {SUPPORT_EMAIL}
              </a>
              <p css={styles.infoBoxHint}>{t(i18nKeys.auth.click_to_copy_and_open_email)}</p>
            </div>

            {/* 로그인 페이지로 돌아가기 버튼 */}
            <Button type="button" variant="primary" width="100%" onClick={() => navigate('/login')}>
              {t(i18nKeys.auth.back_to_login)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindEmail;
